import React, { ReactElement, useLayoutEffect, useState } from 'react';
import { debounce } from '../utils/funcUtils';

type Props = {
  breakpoints: {
    width: number;
    columns: number;
  }[];
  children: ReactElement[] | ReactElement;
};

export const DynamicColumnLayout = ({ breakpoints, children }: Props) => {
  const [columns, setColumns] = useState<Array<ReactElement[]>>([children] as Array<ReactElement[]>);

  const createColumns = (numColumns: number) => {
    const newColumns = Array.from({ length: numColumns }, () => [] as ReactElement[]);
    React.Children.map(children, (child, index) => newColumns[index % numColumns].push(child));
    setColumns(newColumns);
  };

  const determineColumnCount = (bp: typeof breakpoints) => {
    const screenWidth = window.innerWidth;
    const sortedBreakpoints = [...bp].sort((a, b) => b.width - a.width);
    const matchingBreakpoint = sortedBreakpoints.find(b => screenWidth >= b.width);
    return matchingBreakpoint ? matchingBreakpoint.columns : 1;
  };

  const separateChildrenIntoColumns = (bp: typeof breakpoints, currentColumnCount: number) => {
    const newColumnCount = determineColumnCount(bp);
    if (newColumnCount !== currentColumnCount) {
      createColumns(newColumnCount);
    }
  };

  useLayoutEffect(() => {
    separateChildrenIntoColumns(breakpoints, columns.length);

    const handleResize = debounce(() => separateChildrenIntoColumns(breakpoints, columns.length), 200);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [children, breakpoints, columns.length]);

  return columns.map((columnChildren, index) => (
    <div key={index} data-column={index}>
      {columnChildren}
    </div>
  ));
};
